import React, { useState, useRef } from 'react';
import Tesseract from 'tesseract.js';
import useOcrExtractor from '../hooks/useOcrExtractor';
import { HiArrowRight, HiArrowLeft } from 'react-icons/hi2';
import { HiOutlineRefresh } from 'react-icons/hi';
import { FiTrash2 } from 'react-icons/fi';
import { getDeviceDetails } from '../utils/api';

export default function UploadStep({ onNext, onBack, setOcrData, ocrData }) {
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(ocrData?.imageUrl || null);
  const [eui, setEui] = useState(ocrData?.eui || '');
  const [metadata, setMetadata] = useState(ocrData?.metadata || []);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [manualMode, setManualMode] = useState(!!ocrData?.eui);
  const [cameraActive, setCameraActive] = useState(false);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const extractInfoFromText = useOcrExtractor();

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      videoRef.current.srcObject = stream;
      setCameraActive(true);
    } catch (err) {
      setError('Unable to access camera. Please allow permission or use upload instead.');
    }
  };

  const captureImage = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    ctx.drawImage(video, 0, 0);
    canvas.toBlob((blob) => {
      const file = new File([blob], 'capture.jpg', { type: 'image/jpeg' });
      handleSelectedFile(file);
      stopCamera();
    }, 'image/jpeg');
  };

  const stopCamera = () => {
    const stream = videoRef.current.srcObject;
    stream?.getTracks().forEach((track) => track.stop());
    videoRef.current.srcObject = null;
    setCameraActive(false);
  };

  const handleSelectedFile = (file) => {
    const url = URL.createObjectURL(file);
    setSelectedFile(file);
    setPreviewUrl(url);

    setEui('');
    setMetadata([]);
    setManualMode(false);
    setOcrData({ eui: '', imageUrl: url, metadata: [] });
    setError('');
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) handleSelectedFile(file);
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setError('Please upload or capture an image.');
      return;
    }

    setLoading(true);
    try {
      const { data: { text } } = await Tesseract.recognize(previewUrl, 'eng');
      const { devEui, serialNumber, model, fccId, sku } = extractInfoFromText(text);

      const meta = [];
      if (model) meta.push({ label: 'Model', value: model });
      if (fccId) meta.push({ label: 'FCC ID', value: fccId });
      if (serialNumber) meta.push({ label: 'Serial Number', value: serialNumber });
      if (sku) meta.push({ label: 'SKU', value: sku });

      let deviceType = '';
      try {
        const deviceInfo = await getDeviceDetails(devEui);
        deviceType = deviceInfo.deviceType;
      } catch {
        console.warn('No device info found. Proceeding with OCR results only.');
      }

      if (!devEui) {
        setError('Could not find a valid DevEUI. You can continue manually.');
        setManualMode(true);
        setMetadata(meta);
        setLoading(false);
        return;
      }

      setEui(devEui);
      setMetadata(meta);
      setOcrData({ eui: devEui, imageUrl: previewUrl, metadata: meta, deviceType });
      setError('');
    } catch (err) {
      console.error(err);
      setError('Image processing failed. Try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleNext = () => {
    if (!eui.trim()) {
      setError('DevEUI is required.');
      return;
    }
    setOcrData((prev) => ({ ...prev, eui, imageUrl: previewUrl, metadata }));
    onNext();
  };

  const updateMetadata = (index, key, value) => {
    const updated = [...metadata];
    updated[index][key] = value;
    setMetadata(updated);
  };

  const removeMetadata = (index) => setMetadata(metadata.filter((_, i) => i !== index));
  const addMetadataRow = () => setMetadata([...metadata, { label: '', value: '' }]);

  return (
    <div className="space-y-10">
      <h1 className="text-2xl font-semibold text-gray-800">Scan Device Label</h1>

      {/* Buttons for camera or upload */}
      <div className="flex gap-4">
        <button
          onClick={startCamera}
          className="bg-primary text-white px-4 py-2 rounded hover:bg-primary/90 text-sm"
        >
          Scan with Camera
        </button>
        <input type="file" accept="image/*" onChange={handleFileChange} className="hidden" id="fileUpload" />
        <label htmlFor="fileUpload" className="bg-primary text-white px-4 py-2 rounded cursor-pointer hover:bg-primary/90 text-sm">
          Upload from Gallery
        </label>
      </div>

      {/* Camera preview */}
      {cameraActive && (
        <div className="mt-4">
          <video ref={videoRef} autoPlay className="w-full max-h-64 rounded-md border" />
          <canvas ref={canvasRef} className="hidden" />
          <div className="mt-2 flex gap-2">
            <button onClick={captureImage} className="bg-green-600 text-white px-4 py-2 rounded text-sm">
              Capture
            </button>
            <button onClick={stopCamera} className="bg-gray-300 text-gray-800 px-4 py-2 rounded text-sm">
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Preview and OCR trigger */}
      {previewUrl && (
        <div className="mt-4 space-y-3">
          <img src={previewUrl} alt="Preview" className="max-h-64 rounded-md border" />
          {!eui && (
            <button
              onClick={handleUpload}
              disabled={loading}
              className={`inline-flex items-center gap-2 px-4 py-2 text-sm rounded-md transition ${
                loading ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-primary text-white hover:bg-primary/90'
              }`}
            >
              {loading && <HiOutlineRefresh className="animate-spin h-5 w-5" />}
              {loading ? 'Scanning...' : 'Scan Image'}
            </button>
          )}
          {error && <p className="text-sm text-red-600">{error}</p>}
        </div>
      )}

      {/* DevEUI input */}
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">DevEUI</label>
          <input
            type="text"
            value={eui}
            onChange={(e) => setEui(e.target.value)}
            placeholder="Enter or verify DevEUI"
            className="w-full border rounded-md px-4 py-2 text-sm"
          />
        </div>
      </div>

      {/* Metadata block */}
      {(manualMode || metadata.length > 0) && (
        <div className="mt-3 bg-gray-50 border rounded-md p-3 text-sm space-y-2">
          <p className="font-medium text-gray-800 mb-1">Metadata:</p>
          {metadata.map((item, idx) => (
            <div key={idx} className="flex flex-col sm:flex-row sm:items-center gap-2">
              <input
                type="text"
                value={item.label}
                onChange={(e) => updateMetadata(idx, 'label', e.target.value)}
                className="sm:w-1/3 px-2 py-1 border rounded-md text-sm"
              />
              <input
                type="text"
                value={item.value}
                onChange={(e) => updateMetadata(idx, 'value', e.target.value)}
                className="sm:flex-1 px-2 py-1 border rounded-md text-sm"
              />
              <button
                onClick={() => removeMetadata(idx)}
                className="text-red-500 hover:text-red-700"
                title="Remove"
              >
                <FiTrash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={addMetadataRow}
            className="mt-2 text-sm text-primary hover:underline"
          >
            + Add Metadata Field
          </button>
        </div>
      )}

      <div className="pt-6 flex justify-between">
        <button
          onClick={onBack}
          className="inline-flex items-center gap-2 px-5 py-2 rounded-lg bg-gray-200 text-sm text-gray-800 hover:bg-gray-300 transition"
        >
          <HiArrowLeft className="w-4 h-4" />
          Back
        </button>
        <button
          onClick={handleNext}
          className="inline-flex items-center gap-2 px-6 py-2 rounded-lg bg-primary text-white text-sm font-medium hover:bg-primary/90 transition"
        >
          Next
          <HiArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
